import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { comics, categoryList, getComicsByCategory } from "@/mocks/comics";

const siteUrl = "https://vhzhn02.cn";

const sortOptions = [
  { key: "hot", name: "人气榜", icon: "ri-fire-line" },
  { key: "new", name: "最新更新", icon: "ri-time-line" },
  { key: "done", name: "已完结", icon: "ri-check-double-line" },
];

export default function Category() {
  const { tag = "all" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get("q") ?? "";
  const sort = searchParams.get("sort") ?? "hot";
  const keyword = q;

  const activeCategory = categoryList.find((c) => c.key === tag) ?? categoryList[0];

  let list = getComicsByCategory(tag);
  if (keyword.trim()) {
    const kw = keyword.trim().toLowerCase();
    list = list.filter(
      (c) =>
        c.title.toLowerCase().includes(kw) ||
        c.author.toLowerCase().includes(kw) ||
        c.tags.some((t) => t.toLowerCase().includes(kw))
    );
  }
  if (sort === "hot") list = [...list].sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
  if (sort === "new") list = [...list].reverse();
  if (sort === "done") list = list.filter((c) => c.status === "已完结");

  const setSearch = (updates: { q?: string; sort?: string }) => {
    const next = new URLSearchParams(searchParams);
    if (updates.q !== undefined) {
      if (updates.q) next.set("q", updates.q);
      else next.delete("q");
    }
    if (updates.sort !== undefined) next.set("sort", updates.sort);
    setSearchParams(next, { replace: false });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch({ q: keyword });
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${activeCategory.name}漫画 - 漫天堂漫画库`,
    url: `${siteUrl}/category/${tag}`,
    isPartOf: { "@type": "WebSite", name: "漫天堂", url: siteUrl },
    about: `${activeCategory.name}漫画作品索引`,
  };

  return (
    <>
      <JsonLd data={[schema]} />
      <Navbar />

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ name: "漫画库", path: "/category/all" }, { name: activeCategory.name }]} />

        {/* Page title */}
        <div className="mt-4 mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {activeCategory.name}漫画
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              共 {list.length} 部作品 · 分类浏览与站内检索，快速定位想看的漫画
            </p>
          </div>

          {/* Search */}
          <form
            onSubmit={handleSearch}
            role="search"
            className="flex w-full items-center gap-2 rounded-full bg-background-100 px-3 py-1.5 focus-within:ring-2 focus-within:ring-primary-400 md:w-72"
          >
            <i className="ri-search-line text-foreground-500" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setSearch({ q: e.target.value })}
              placeholder="在本分类搜索"
              className="w-full bg-transparent py-1 text-sm outline-none placeholder:text-foreground-400"
              aria-label="分类内搜索"
            />
            <button type="submit" className="whitespace-nowrap text-sm text-primary-600">
              搜索
            </button>
          </form>
        </div>

        {/* Category switch */}
        <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="漫画分类切换">
          {categoryList.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}${q ? `?q=${encodeURIComponent(q)}` : ""}`}
              role="tab"
              aria-selected={tag === c.key}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                tag === c.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              <i className={c.icon} />
              {c.name}
            </Link>
          ))}
        </div>

        {/* Sort tabs */}
        <div className="mb-6 flex gap-2 rounded-full bg-background-100 p-1 px-1 py-1 w-fit">
          {sortOptions.map((s) => (
            <button
              key={s.key}
              onClick={() => setSearch({ sort: s.key })}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                sort === s.key ? "bg-background-50 text-primary-700" : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className={s.icon} />
              {s.name}
            </button>
          ))}
        </div>

        {/* Result */}
        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {list.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-background-200/70 bg-background-50 py-16 text-center">
            <i className="ri-file-search-line text-4xl text-foreground-400" />
            <p className="text-sm text-foreground-600">没有找到匹配的作品，换个关键词试试吧</p>
            <button
              onClick={() => navigate("/category/all")}
              className="rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              浏览全部漫画
            </button>
          </div>
        )}
      </div>

      <Footer />
    </>
  );
}